import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UilCommentAltDots, UilTimes, UilCheckCircle,
  UilUser, UilPhone, UilEnvelope, UilMessage, UilClock
} from '@iconscout/react-unicons';
import Logo from '../assets/Images/logo.png';
import { contactService } from '../services/contact/contactService';

export default function ChatNow() {
  const [isOpen, setIsOpen] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', projectDescription: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [showBadge, setShowBadge] = useState(false);

  const isBusinessHours = () => { const h = new Date().getHours(); return h >= 7 && h < 18; };

  useEffect(() => {
    setShowBadge(isBusinessHours());
    const iv = setInterval(() => setShowBadge(isBusinessHours()), 60 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!autoOpened) {
      const t = setTimeout(() => { setIsOpen(true); setShowBadge(false); setAutoOpened(true); }, 12000);
      return () => clearTimeout(t);
    }
  }, [autoOpened]);

  const toggle = () => { setIsOpen(!isOpen); setShowBadge(false); setAutoOpened(true); };

  const handleChange = (e) => setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, phone, email, projectDescription } = formData;
    if (!name || !phone || !email || !projectDescription || !isAgreed) {
      return alert('Please complete all fields and agree to terms');
    }
    setIsLoading(true);
    const result = await contactService.sendChatRequest(formData);
    if (result.success) {
      setIsSubmitted(true);
      setFormData({ name: '', phone: '', email: '', projectDescription: '' });
      setIsAgreed(false);
    } else {
      alert(result.error || 'Failed to send — please try again');
    }
    setIsLoading(false);
  };

  const inputCls = "w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors";

  return (
    <>
      {/* Toggle Button */}
      <motion.button
        onClick={toggle}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 bg-brand-600 hover:bg-brand-700 text-white p-4 rounded-full shadow-lg z-50 transition-colors"
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        <AnimatePresence mode="wait">
          {isOpen
            ? <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}><UilTimes size={22} /></motion.div>
            : <motion.div key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}><UilCommentAltDots size={22} /></motion.div>
          }
        </AnimatePresence>
        {showBadge && !isOpen && (
          <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-amber-400" />
          </span>
        )}
      </motion.button>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.aside
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-6 w-[360px] max-w-[calc(100vw-1.5rem)] bg-white rounded-3xl shadow-2xl overflow-hidden z-50 border border-slate-100"
          >
            {/* Header */}
            <div className="bg-brand-600 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img src={Logo} alt="Faby Cleaners" className="w-9 h-9 rounded-xl object-cover" />
                  {isBusinessHours() && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 border-2 border-brand-600 rounded-full" />
                  )}
                </div>
                <div className="text-white">
                  <p className="text-sm font-bold leading-tight">Faby Cleaners</p>
                  <p className="text-xs text-brand-100 flex items-center gap-1">
                    <UilClock size={10} />
                    {isBusinessHours() ? 'Online now' : "We'll reply soon"}
                  </p>
                </div>
              </div>
              <button onClick={toggle} className="p-1.5 rounded-lg hover:bg-brand-700 text-white transition-colors">
                <UilTimes size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 max-h-[480px] overflow-y-auto bg-slate-50">
              <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }} className="mb-5">
                <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm inline-block max-w-[85%]">
                  <p className="text-sm text-slate-700">Hello! How can we help make your space shine today? 🌟</p>
                </div>
                <p className="text-xs text-slate-400 mt-1 ml-1">{new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</p>
              </motion.div>

              {isSubmitted ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6">
                  <div className="w-14 h-14 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <UilCheckCircle size={28} className="text-brand-600" />
                  </div>
                  <p className="font-bold text-slate-900 mb-1">Message sent!</p>
                  <p className="text-sm text-slate-500">We'll reach out via SMS shortly.</p>
                  <button onClick={() => setIsSubmitted(false)}
                    className="mt-5 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors">
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <motion.form onSubmit={handleSubmit} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="space-y-3">
                  {[
                    { name: 'name',               type: 'text',  placeholder: 'Your name',     Icon: UilUser },
                    { name: 'phone',              type: 'tel',   placeholder: 'Phone number',  Icon: UilPhone },
                    { name: 'email',              type: 'email', placeholder: 'Email address', Icon: UilEnvelope },
                  ].map(({ name, type, placeholder, Icon }) => (
                    <div key={name} className="relative">
                      <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input name={name} type={type} placeholder={placeholder} required value={formData[name]} onChange={handleChange} className={inputCls} />
                    </div>
                  ))}
                  <div className="relative">
                    <UilMessage size={14} className="absolute left-3 top-3 text-slate-400" />
                    <textarea name="projectDescription" placeholder="How can we help you?" required rows={3}
                      value={formData.projectDescription} onChange={handleChange}
                      className={`${inputCls} resize-none`} />
                  </div>

                  <label className="flex items-start gap-2 text-xs text-slate-500 cursor-pointer">
                    <input type="checkbox" checked={isAgreed} onChange={() => setIsAgreed(!isAgreed)} className="mt-0.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                    <span>I agree to receive messages per our <a href="/privacy" className="text-brand-600 hover:underline">Privacy Policy</a>.</span>
                  </label>

                  <button type="submit" disabled={isLoading || !isAgreed}
                    className="w-full bg-brand-600 hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-semibold py-3 rounded-xl transition-colors">
                    {isLoading ? 'Sending…' : 'Send Message'}
                  </button>
                </motion.form>
              )}
            </div>

            <div className="bg-white px-5 py-3 border-t border-slate-100">
              <p className="text-xs text-slate-400 text-center">Typically replies within a few minutes</p>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
